import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import TranslateRoundedIcon from '@mui/icons-material/TranslateRounded';
import { Box, Button, Chip, Collapse, MenuItem, Select, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import React from 'react';
import { LanguageLevelEnum, LanguageLevelLabels } from '../../../api/ChatApi';
import { customPedagogyGuidanceId, defaultPedagogyGuidance, pedagogyGuidanceOptions, PedagogyGuidanceSelectValue } from '../pedagogyGuidance';
import { aiButtonSx, BLUE, BLUE_TEXT, BORDER, cardSx, GREEN, INTER, NAVY, POPPINS, SLATE, SOFT_BLUE, SUCCESS_TEXT, SURFACE_MUTED, SURFACE_TINT } from '../teacherStyles';

const actflLevels = [
    LanguageLevelEnum.ActflNoviceLow,
    LanguageLevelEnum.ActflNoviceMid,
    LanguageLevelEnum.ActflNoviceHigh,
    LanguageLevelEnum.ActflIntermediateLow,
    LanguageLevelEnum.ActflIntermediateMid,
    LanguageLevelEnum.ActflIntermediateHigh,
    LanguageLevelEnum.ActflAdvancedLow,
    LanguageLevelEnum.ActflAdvancedMid,
    LanguageLevelEnum.ActflAdvancedHigh,
    LanguageLevelEnum.ActflSuperior,
    LanguageLevelEnum.ActflDistinguished,
];

interface TeacherBriefProps {
    brief: string;
    advancedContext: string;
    onBriefChange: (value: string) => void;
    onAdvancedContextChange: (value: string) => void;
    onCreateOutline: () => void;
}

type TargetMode = 'suggest' | 'provide' | 'hybrid';

interface LanguageTargetFieldProps {
    title: string;
    icon: React.ElementType;
    defaultValue: string;
    placeholder: string;
}

function LanguageTargetField({ title, icon: Icon, defaultValue, placeholder }: LanguageTargetFieldProps) {
    const [mode, setMode] = React.useState<TargetMode>('hybrid');

    return (
        <Box sx={{ minWidth: 0, p: { xs: 2, sm: 2.25 }, border: `1px solid ${BORDER}`, borderRadius: '16px', backgroundColor: SURFACE_TINT }}>
            <Stack direction="row" alignItems="center" spacing={1.25}>
                <Box sx={{ width: 38, height: 38, flexShrink: 0, borderRadius: '12px', display: 'grid', placeItems: 'center', color: BLUE, backgroundColor: SOFT_BLUE }}>
                    <Icon sx={{ fontSize: 20 }} />
                </Box>
                <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, color: NAVY, fontSize: 15 }}>{title}</Typography>
            </Stack>

            <ToggleButtonGroup
                exclusive
                size="small"
                value={mode}
                onChange={(_event, value) => value && setMode(value)}
                sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 0.75, mt: 2, '& .MuiToggleButtonGroup-grouped': { minWidth: 0, border: `1px solid ${BORDER} !important`, borderRadius: '10px !important', px: 0.75, fontFamily: INTER, fontWeight: 700, fontSize: 11.5, lineHeight: 1.25, textTransform: 'none' } }}
            >
                <ToggleButton value="suggest">AI suggest</ToggleButton>
                <ToggleButton value="provide">I’ll provide</ToggleButton>
                <ToggleButton value="hybrid">Mine + AI</ToggleButton>
            </ToggleButtonGroup>

            {mode !== 'suggest' && (
                <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    defaultValue={defaultValue}
                    placeholder={placeholder}
                    inputProps={{ 'aria-label': `${title} to include` }}
                    sx={{ mt: 1.5, '& .MuiOutlinedInput-root': { alignItems: 'flex-start', backgroundColor: '#fff' }, '& .MuiInputBase-input': { fontFamily: INTER, fontSize: 14, lineHeight: 1.5 } }}
                />
            )}

            <Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{ mt: 1.5 }}>
                <AutoAwesomeRoundedIcon sx={{ mt: 0.1, color: mode === 'provide' ? SLATE : GREEN, fontSize: 16 }} />
                <Typography sx={{ fontFamily: INTER, color: mode === 'provide' ? SLATE : SUCCESS_TEXT, fontWeight: 600, fontSize: 11.5, lineHeight: 1.45 }}>
                    {mode === 'provide'
                        ? `Only the ${title.toLowerCase()} you enter will be included.`
                        : mode === 'suggest'
                            ? `AI will propose level-appropriate ${title.toLowerCase()} for approval.`
                            : `Your ${title.toLowerCase()} will be required, and AI can suggest more.`}
                </Typography>
            </Stack>
        </Box>
    );
}

export default function LessonDescriptionEditor({ brief, advancedContext, onBriefChange, onAdvancedContextChange, onCreateOutline }: TeacherBriefProps) {
    const [advancedOpen, setAdvancedOpen] = React.useState(false);
    const [selectedPedagogy, setSelectedPedagogy] = React.useState<PedagogyGuidanceSelectValue>(defaultPedagogyGuidance.id);
    const selectedPedagogyPreset = pedagogyGuidanceOptions.find(option => option.id === selectedPedagogy);
    const isSelectedPedagogyApplied = selectedPedagogyPreset?.context === advancedContext;

    const handlePedagogyChange = (event: SelectChangeEvent<PedagogyGuidanceSelectValue>) => {
        const selected = event.target.value as PedagogyGuidanceSelectValue;
        setSelectedPedagogy(selected);
    };

    const applyPedagogyPreset = () => {
        if (selectedPedagogyPreset) {
            onAdvancedContextChange(selectedPedagogyPreset.context);
        }
    };

    const handleAdvancedContextChange = (value: string) => {
        onAdvancedContextChange(value);
        setSelectedPedagogy(customPedagogyGuidanceId);
    };

    const addAdvancedGuidance = (guidance: string) => {
        const current = advancedContext.trim();
        onAdvancedContextChange(`${current}${current ? '\n' : ''}${guidance}.`);
        setAdvancedOpen(true);
    };

    return (
        <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: { xs: 4, md: 7 } }}>
            <Box sx={{ width: '100%', maxWidth: 920, mx: 'auto' }}>
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Box sx={{ width: 48, height: 48, mx: 'auto', mb: 2, borderRadius: '16px', display: 'grid', placeItems: 'center', backgroundColor: SOFT_BLUE }}>
                        <AutoAwesomeRoundedIcon sx={{ color: BLUE }} />
                    </Box>
                    <Typography component="h1" sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: { xs: 28, md: 36 }, color: NAVY, letterSpacing: '-0.03em' }}>
                        What would you like to teach?
                    </Typography>
                </Box>

                <Box sx={{ ...cardSx, p: { xs: 2.5, md: 3.5 } }}>
                    <TextField
                        fullWidth
                        multiline
                        minRows={5}
                        value={brief}
                        onChange={event => onBriefChange(event.target.value)}
                        placeholder="Create a 50-minute Mandarin lesson about..."
                        inputProps={{ 'aria-label': 'Describe your lesson' }}
                        sx={{ '& .MuiOutlinedInput-root': { alignItems: 'flex-start', fontFamily: INTER, fontSize: 17, lineHeight: 1.65, backgroundColor: SURFACE_TINT } }}
                    />

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mt: 2.5 }}>
                        <Select defaultValue={LanguageLevelEnum.ActflIntermediateMid} size="small" inputProps={{ 'aria-label': 'ACTFL proficiency level' }} sx={{ minWidth: 220, fontFamily: INTER, backgroundColor: '#fff' }}>
                            {actflLevels.map(level => (
                                <MenuItem key={level} value={level} sx={{ fontFamily: INTER }}>{LanguageLevelLabels[level]}</MenuItem>
                            ))}
                        </Select>
                        <Select defaultValue="50 minutes" size="small" sx={{ minWidth: 145, fontFamily: INTER, backgroundColor: '#fff' }}>
                            <MenuItem value="30 minutes">30 minutes</MenuItem>
                            <MenuItem value="45 minutes">45 minutes</MenuItem>
                            <MenuItem value="50 minutes">50 minutes</MenuItem>
                            <MenuItem value="75 minutes">75 minutes</MenuItem>
                        </Select>
                        <Select defaultValue="Simplified" size="small" sx={{ minWidth: 145, fontFamily: INTER, backgroundColor: '#fff' }}>
                            <MenuItem value="Simplified">Simplified</MenuItem>
                            <MenuItem value="Traditional">Traditional</MenuItem>
                        </Select>
                    </Stack>
                </Box>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ justifyContent: 'center', mt: 2.5 }}>
                    <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, alignSelf: 'center', mr: 0.5 }}>Try adding:</Typography>
                    {['More speaking practice', 'Keep language ACTFL-aligned', 'Include pair work', 'Add an exit ticket'].map(label => (
                        <Chip key={label} label={label} onClick={() => onBriefChange(`${brief.trim()} ${label}.`)} sx={{ fontFamily: INTER, color: BLUE_TEXT, backgroundColor: SOFT_BLUE }} />
                    ))}
                </Stack>

                <Box sx={{ ...cardSx, p: { xs: 2.5, md: 3.5 }, mt: 4 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }}>
                        <Box sx={{ width: 42, height: 42, flexShrink: 0, borderRadius: '14px', display: 'grid', placeItems: 'center', backgroundColor: SOFT_BLUE }}>
                            <TipsAndUpdatesOutlinedIcon sx={{ color: BLUE }} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 17, color: NAVY }}>Language targets</Typography>
                                <Chip label="Optional" size="small" sx={{ fontFamily: INTER, color: SLATE, backgroundColor: SURFACE_MUTED }} />
                            </Stack>
                        </Box>
                    </Stack>

                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2, mt: 2.5 }}>
                        <LanguageTargetField
                            title="Vocabulary"
                            icon={TranslateRoundedIcon}
                            defaultValue="菜单, 服务员"
                            placeholder="Enter words or paste a vocabulary list…"
                        />
                        <LanguageTargetField
                            title="Grammar"
                            icon={AccountTreeOutlinedIcon}
                            defaultValue="请给我……"
                            placeholder="Enter grammar patterns or teaching goals…"
                        />
                    </Box>
                </Box>

                <Box sx={{ ...cardSx, mt: 2.5, overflow: 'hidden' }}>
                    <Button
                        fullWidth
                        onClick={() => setAdvancedOpen(open => !open)}
                        aria-expanded={advancedOpen}
                        sx={{ justifyContent: 'flex-start', p: { xs: 2.5, md: 3 }, textAlign: 'left', textTransform: 'none', borderRadius: '20px', color: NAVY }}
                    >
                        <Box sx={{ width: 42, height: 42, mr: 1.5, flexShrink: 0, borderRadius: '14px', display: 'grid', placeItems: 'center', backgroundColor: SURFACE_MUTED }}>
                            <TuneRoundedIcon sx={{ color: BLUE, fontSize: 21 }} />
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 16, color: NAVY }}>Advanced AI guidance</Typography>
                                <Chip label="Optional" size="small" sx={{ fontFamily: INTER, color: SLATE, backgroundColor: SURFACE_MUTED }} />
                            </Stack>
                            <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 12.5, lineHeight: 1.45, mt: 0.35 }}>
                                Pedagogy, classroom routines, and constraints sent directly to the lesson-building AI.
                            </Typography>
                        </Box>
                        <Typography sx={{ ml: 1.5, fontFamily: INTER, fontWeight: 700, fontSize: 12, color: BLUE }}>{advancedOpen ? 'Hide' : 'Edit'}</Typography>
                    </Button>

                    <Collapse in={advancedOpen}>
                        <Box sx={{ px: { xs: 2.5, md: 3 }, pb: { xs: 2.5, md: 3 } }}>
                            <Box sx={{ height: 1, backgroundColor: BORDER, mb: 2.5 }} />
                            <Typography sx={{ fontFamily: INTER, fontWeight: 700, color: SLATE, fontSize: 11.5, mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Pedagogy approach</Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mb: 0.75 }}>
                                <Select
                                    size="small"
                                    value={selectedPedagogy}
                                    onChange={handlePedagogyChange}
                                    inputProps={{ 'aria-label': 'Pedagogy approach' }}
                                    sx={{ width: { xs: '100%', sm: 360 }, maxWidth: '100%', fontFamily: INTER, backgroundColor: '#fff' }}
                                >
                                    {pedagogyGuidanceOptions.map(option => (
                                        <MenuItem key={option.id} value={option.id} sx={{ fontFamily: INTER }}>{option.label}</MenuItem>
                                    ))}
                                    <MenuItem value={customPedagogyGuidanceId} sx={{ fontFamily: INTER }}>Custom</MenuItem>
                                </Select>
                                <Button
                                    variant="contained"
                                    disabled={!selectedPedagogyPreset || isSelectedPedagogyApplied}
                                    onClick={applyPedagogyPreset}
                                    sx={{ alignSelf: { xs: 'flex-start', sm: 'stretch' }, px: 2, boxShadow: 'none', textTransform: 'none', fontFamily: INTER, fontWeight: 700, backgroundColor: BLUE, '&:hover': { backgroundColor: BLUE_TEXT, boxShadow: 'none' } }}
                                >
                                    Apply preset
                                </Button>
                            </Stack>
                            <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 11.5, lineHeight: 1.5, mb: 2 }}>
                                Choose a methodology, then click Apply preset to update the context below. Editing the text keeps your custom changes until you apply a preset again.
                            </Typography>
                            <Typography sx={{ fontFamily: INTER, fontWeight: 700, color: SLATE, fontSize: 11.5, mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Additional AI context</Typography>
                            <TextField
                                fullWidth
                                multiline
                                minRows={8}
                                maxRows={8}
                                value={advancedContext}
                                onChange={event => handleAdvancedContextChange(event.target.value)}
                                placeholder="Example: Use task-based learning. Prioritize authentic communication and student talk time…"
                                inputProps={{ 'aria-label': 'Advanced AI lesson-building context' }}
                                sx={{ '& .MuiOutlinedInput-root': { alignItems: 'flex-start', backgroundColor: SURFACE_TINT }, '& .MuiInputBase-input': { fontFamily: INTER, fontSize: 14, lineHeight: 1.55, overflowY: 'auto !important', resize: 'vertical' } }}
                            />
                            <Typography sx={{ fontFamily: INTER, color: SLATE, fontSize: 11.5, lineHeight: 1.5, mt: 1 }}>
                                This guidance shapes AI generation but does not appear automatically in the printable lesson plan.
                            </Typography>
                            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                                {['Prioritize student talk time', 'Support mixed proficiency', 'Include formative retry', 'Limit explicit grammar explanation'].map(label => (
                                    <Chip key={label} label={label} onClick={() => addAdvancedGuidance(label)} size="small" sx={{ fontFamily: INTER, fontSize: 11, color: BLUE_TEXT, backgroundColor: SOFT_BLUE }} />
                                ))}
                            </Stack>
                        </Box>
                    </Collapse>
                </Box>

                <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
                    <Button
                        variant="contained"
                        endIcon={<ArrowForwardRoundedIcon />}
                        disabled={brief.trim().length === 0}
                        onClick={onCreateOutline}
                        sx={{ ...aiButtonSx, px: 3, py: 1.25 }}
                    >
                        Generate lesson plan
                    </Button>
                </Stack>

                <Box sx={{ mt: 7 }}>
                    <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 17, color: NAVY, mb: 1.5 }}>
                        Start another way
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <Button disabled startIcon={<DescriptionOutlinedIcon />} sx={{ flex: 1, justifyContent: 'flex-start', p: 2, border: `1px solid ${BORDER}`, backgroundColor: '#fff', color: SLATE, fontFamily: INTER }}>
                            Build from a document · Coming later
                        </Button>
                        <Button disabled startIcon={<HistoryRoundedIcon />} sx={{ flex: 1, justifyContent: 'flex-start', p: 2, border: `1px solid ${BORDER}`, backgroundColor: '#fff', color: SLATE, fontFamily: INTER }}>
                            Adapt an existing lesson · Coming later
                        </Button>
                    </Stack>
                </Box>
            </Box>
        </Box>
    );
}
